import 'react-toastify/dist/ReactToastify.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import {
  AuthContextProvider,
  ClientCurrentOrders,
  ClientProtectedRoute,
  PageLoader,
} from './components';
import { RoutePath } from './config';
import { useAuthContext } from './hooks';
import {
  ClientBonusPage,
  ClientCalendarPage,
  ClientCreateOrderPage,
  ClientLoginPage,
  ClientMedicinePage,
  ClientOrderHistoryPage,
  ClientProfilePage,
  ClientSettingsPage,
  ClientSignupPage,
  DoctorCreateOrderPage,
  DoctorCreatePatientPage,
  DoctorEditPatientPage,
  DoctorLoginPage,
  DoctorMedicinePage,
  DoctorOrderHistoryItemPage,
  DoctorOrderHistoryPage,
  DoctorPatientDetailsPage,
  DoctorPatientOrderDetailsPage,
  DoctorPatientsPage,
  LandingFormPage,
} from './pages';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/lk">
        <AuthContextProvider>
          <Pages />
        </AuthContextProvider>
      </BrowserRouter>
      <ToastContainer />
    </QueryClientProvider>
  );
};

const Pages = () => {
  const { isAuthorized, isClient, isLoading } = useAuthContext();
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate replace to={RoutePath.ClientLogin} />}
      />
      <Route path={RoutePath.ClientLogin} element={<ClientLoginPage />} />
      <Route path={RoutePath.DoctorLogin} element={<DoctorLoginPage />} />
      <Route path={RoutePath.ClientRegister} element={<ClientSignupPage />} />
      <Route path={RoutePath.Landing} element={<LandingFormPage />} />
      <Route
        path="/order/now"
        element={<Navigate replace to={RoutePath.Landing} />}
      />
      <Route
        path={RoutePath.ClientCurrentOrder}
        element={
          <ClientProtectedRoute>
            <ClientCurrentOrders />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientCreateOrder}
        element={
          <ClientProtectedRoute>
            <ClientCreateOrderPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientMedicine}
        element={
          <ClientProtectedRoute>
            <ClientMedicinePage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientCalendar}
        element={
          <ClientProtectedRoute>
            <ClientCalendarPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientOrderHistory}
        element={
          <ClientProtectedRoute>
            <ClientOrderHistoryPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientSettings}
        element={
          <ClientProtectedRoute>
            <ClientSettingsPage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientProfile}
        element={
          <ClientProtectedRoute>
            <ClientProfilePage />
          </ClientProtectedRoute>
        }
      />
      <Route
        path={RoutePath.ClientBonus}
        element={
          <ClientProtectedRoute>
            <ClientBonusPage />
          </ClientProtectedRoute>
        }
      />

      {isAuthorized && (
        <Route
          path={RoutePath.DoctorCreateOrder}
          element={<DoctorCreateOrderPage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorPatients}
          element={<DoctorPatientsPage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorCreatePatient}
          element={<DoctorCreatePatientPage />}
        />
      )}
      {/* {isAuthorized && <Route path={RoutePath.DoctorCreateAddress} element={<DoctorCreateAddressPage />} />} */}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorPatientDetails}
          element={<DoctorPatientDetailsPage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorEditPatient}
          element={<DoctorEditPatientPage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorPatientOrderDetails}
          element={<DoctorPatientOrderDetailsPage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorMedicine}
          element={<DoctorMedicinePage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorOrderHistory}
          element={<DoctorOrderHistoryPage />}
        />
      )}
      {isAuthorized && (
        <Route
          path={RoutePath.DoctorOrderHistoryItem}
          element={<DoctorOrderHistoryItemPage />}
        />
      )}

      <Route
        path="*"
        element={
          <Navigate
            replace
            to={isClient ? RoutePath.ClientLogin : RoutePath.DoctorLogin}
          />
        }
      />
    </Routes>
  );
};
